import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { useSchool } from '../context/SchoolContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Clock, Plus, Trash2, Bell, Upload, Image, Map, X, Users, FileText, Save } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const SchoolInfoPage = () => {
  const { selectedSchool } = useSchool();
  const [bellTimes, setBellTimes] = useState([]);
  const [schoolImages, setSchoolImages] = useState([]);
  const [schoolNotes, setSchoolNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bellDialogOpen, setBellDialogOpen] = useState(false);
  const [newBell, setNewBell] = useState({ name: '', time: '' });
  const [uploading, setUploading] = useState({ bellTimes: false, map: false });
  const bellTimesFileRef = useRef(null);
  const mapFileRef = useRef(null);

  const fetchData = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    try {
      const [bellsRes, imagesRes, notesRes] = await Promise.all([
        fetch(`${API}/bell-times/${selectedSchool.school_id}`, { credentials: 'include' }),
        fetch(`${API}/school-images/${selectedSchool.school_id}`, { credentials: 'include' }),
        fetch(`${API}/school-notes/${selectedSchool.school_id}`, { credentials: 'include' })
      ]);
      if (bellsRes.ok) setBellTimes(await bellsRes.json());
      if (imagesRes.ok) setSchoolImages(await imagesRes.json());
      if (notesRes.ok) { const d = await notesRes.json(); setSchoolNotes(d.notes || ''); }
    } catch (error) { console.error('Failed to fetch data:', error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [selectedSchool]);

  const handleAddBellTime = async () => {
    if (!newBell.name.trim() || !newBell.time) { toast.error('Name and time are required'); return; }
    try {
      const response = await fetch(`${API}/bell-times`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newBell, school_id: selectedSchool.school_id })
      });
      if (response.ok) { toast.success('Bell time added'); setBellDialogOpen(false); setNewBell({ name: '', time: '' }); fetchData(); }
    } catch (error) { toast.error('Failed to add bell time'); }
  };

  const handleDeleteBellTime = async (bellId) => {
    if (!window.confirm('Delete this bell time?')) return;
    try {
      const r = await fetch(`${API}/bell-times/${bellId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { toast.success('Bell time deleted'); fetchData(); }
    } catch (error) { toast.error('Failed to delete bell time'); }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/school-notes/${selectedSchool.school_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ notes: schoolNotes })
      });
      if (r.ok) toast.success('Notes saved'); else toast.error('Failed to save notes');
    } catch (error) { toast.error('Failed to save notes'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { toast.error('Only JPEG and PNG files are allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = type === 'bellTimes' ? `${API}/school-images/${selectedSchool.school_id}/bell-times` : `${API}/school-images/${selectedSchool.school_id}/map`;
      const r = await fetch(endpoint, { method: 'POST', credentials: 'include', body: formData });
      if (r.ok) { toast.success(`${type === 'bellTimes' ? 'Bell times' : 'School map'} image uploaded`); fetchData(); }
      else { const error = await r.json(); toast.error(error.detail || 'Upload failed'); }
    } catch (error) { toast.error('Failed to upload image'); }
    finally { setUploading(prev => ({ ...prev, [type]: false })); }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const r = await fetch(`${API}/school-images/${imageId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { toast.success('Image deleted'); fetchData(); }
    } catch (error) { toast.error('Failed to delete image'); }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const bellTimesImage = schoolImages.find(img => img.image_type === 'bell_times');
  const schoolMapImage = schoolImages.find(img => img.image_type === 'school_map');

  return (
    <Layout title="School Information" breadcrumb={[{ label: 'School Info' }]}>
      <div className="space-y-6" data-testid="school-info-page">
        <div className="bg-white rounded-xl border border-google-grey-200 p-6">
          <h1 className="text-2xl font-heading font-medium text-google-grey-900 mb-1">{selectedSchool?.name}</h1>
          {selectedSchool?.address && <p className="text-google-grey-600">{selectedSchool.address}</p>}
        </div>

        <Tabs defaultValue="notes" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="notes" className="flex items-center gap-2"><FileText className="w-4 h-4" />Notes & Contacts</TabsTrigger>
            <TabsTrigger value="bell-times" className="flex items-center gap-2"><Bell className="w-4 h-4" />Bell Times</TabsTrigger>
            <TabsTrigger value="maps" className="flex items-center gap-2"><Map className="w-4 h-4" />Maps & Images</TabsTrigger>
          </TabsList>

          <TabsContent value="notes" className="space-y-6">
            <Card className="bg-white border-google-grey-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Users className="w-5 h-5 text-google-blue" />School Notes & Teacher Contacts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-google-grey-500 mb-4">Record important information like teacher names, email addresses, room numbers, or any other notes about this school.</p>
                <Textarea placeholder={"Example:\nPrincipal: Mrs. Smith - principal@school.edu\nYear 5 Coordinator: Mr. Jones - jones@school.edu - Room 12\nStaff Room: Building B, Ground Floor\nWiFi Password: SchoolGuest2024\nImportant: Fire assembly point is the oval"} value={schoolNotes} onChange={(e) => setSchoolNotes(e.target.value)} className="min-h-[300px] border-google-grey-300 font-mono text-sm" data-testid="school-notes-textarea" />
                <Button onClick={handleSaveNotes} disabled={saving} className="mt-4 bg-google-blue hover:bg-blue-600 text-white" data-testid="save-notes-btn">
                  <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Notes'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bell-times" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-white border-google-grey-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading flex items-center gap-2"><Image className="w-5 h-5 text-google-yellow" />Bell Times Image</CardTitle>
                </CardHeader>
                <CardContent>
                  {bellTimesImage ? (
                    <div className="relative">
                      <img src={`data:${bellTimesImage.content_type};base64,${bellTimesImage.data}`} alt="Bell Times" className="w-full rounded-lg border border-google-grey-200 max-h-80 object-contain bg-google-grey-50" data-testid="bell-times-image" />
                      <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => handleDeleteImage(bellTimesImage.image_id)} data-testid="delete-bell-times-image"><X className="w-4 h-4" /></Button>
                      <p className="text-xs text-google-grey-500 mt-2">{bellTimesImage.filename}</p>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-google-grey-300 rounded-lg p-8 text-center hover:border-google-blue hover:bg-blue-50/50 transition-colors cursor-pointer" onClick={() => bellTimesFileRef.current?.click()} data-testid="bell-times-upload-area">
                      <Upload className="w-10 h-10 text-google-grey-300 mx-auto mb-3" />
                      <p className="text-google-grey-700 font-medium mb-1">Upload bell times image</p>
                      <p className="text-sm text-google-grey-400">JPEG or PNG, max 10MB</p>
                    </div>
                  )}
                  <input ref={bellTimesFileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => handleImageUpload('bellTimes', e.target.files?.[0])} data-testid="bell-times-file-input" />
                  {!bellTimesImage && (
                    <Button variant="outline" className="w-full mt-3 border-google-grey-300" onClick={() => bellTimesFileRef.current?.click()} disabled={uploading.bellTimes} data-testid="upload-bell-times-btn">
                      {uploading.bellTimes ? 'Uploading...' : <><Upload className="w-4 h-4 mr-2" />Choose File</>}
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-google-grey-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-heading flex items-center gap-2"><Bell className="w-5 h-5 text-google-yellow" />Bell Schedule</CardTitle>
                    <Dialog open={bellDialogOpen} onOpenChange={setBellDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-google-grey-300" data-testid="add-bell-btn"><Plus className="w-4 h-4" /></Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white">
                        <DialogHeader><DialogTitle className="font-heading text-xl text-google-grey-900">Add Bell Time</DialogTitle></DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <label className="text-sm font-medium text-google-grey-700">Name *</label>
                            <Input placeholder="e.g., Period 1 Start" value={newBell.name} onChange={(e) => setNewBell({ ...newBell, name: e.target.value })} className="mt-1 border-google-grey-300" data-testid="bell-name-input" />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-google-grey-700">Time *</label>
                            <Input type="time" value={newBell.time} onChange={(e) => setNewBell({ ...newBell, time: e.target.value })} className="mt-1 border-google-grey-300" data-testid="bell-time-input" />
                          </div>
                          <Button onClick={handleAddBellTime} className="w-full bg-google-blue hover:bg-blue-600 text-white" data-testid="create-bell-btn">Add Bell Time</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {bellTimes.length === 0 ? (
                    <p className="text-google-grey-500 text-sm py-4 text-center">No bell times set</p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {bellTimes.map((bell) => (
                        <div key={bell.bell_id} className="flex items-center justify-between p-3 bg-google-grey-50 rounded-lg" data-testid={`bell-${bell.bell_id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center"><Clock className="w-4 h-4 text-google-yellow" /></div>
                            <div>
                              <p className="font-medium text-google-grey-800 text-sm">{bell.name}</p>
                              <p className="text-xs text-google-grey-500">{formatTime(bell.time)}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="text-google-grey-400 hover:text-google-red" onClick={() => handleDeleteBellTime(bell.bell_id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="bg-white border-google-grey-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-google-blue" />Today's Schedule
                  <span className="text-sm font-normal text-google-grey-500">({new Date().toLocaleDateString('en-US', { weekday: 'long' })})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bellTimes.length === 0 ? (
                  <div className="py-8 text-center">
                    <Clock className="w-12 h-12 text-google-grey-200 mx-auto mb-3" />
                    <p className="text-google-grey-500">Add bell times to see today's schedule</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-google-grey-200"></div>
                    <div className="space-y-4">
                      {bellTimes.map((bell, index) => {
                        const now = new Date();
                        const bellTime = new Date();
                        const [hours, mins] = bell.time.split(':');
                        bellTime.setHours(parseInt(hours), parseInt(mins), 0);
                        const isPast = now > bellTime;
                        const isCurrent = index < bellTimes.length - 1 && now >= bellTime && now < new Date(bellTime.getTime() + 60 * 60 * 1000);
                        return (
                          <div key={bell.bell_id} className="relative pl-10">
                            <div className={`absolute left-2.5 w-3 h-3 rounded-full ${isCurrent ? 'bg-google-blue ring-4 ring-blue-100' : isPast ? 'bg-google-grey-300' : 'bg-white border-2 border-google-grey-300'}`} />
                            <div className={`p-3 rounded-lg ${isCurrent ? 'bg-blue-50 border border-blue-200' : 'bg-google-grey-50'}`}>
                              <div className="flex justify-between items-center">
                                <span className={`font-medium ${isCurrent ? 'text-google-blue' : 'text-google-grey-700'}`}>{bell.name}</span>
                                <span className={`text-sm ${isCurrent ? 'text-google-blue' : 'text-google-grey-500'}`}>{formatTime(bell.time)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="maps" className="space-y-6">
            <Card className="bg-white border-google-grey-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading flex items-center gap-2"><Map className="w-5 h-5 text-google-green" />Map of School</CardTitle>
              </CardHeader>
              <CardContent>
                {schoolMapImage ? (
                  <div className="relative">
                    <img src={`data:${schoolMapImage.content_type};base64,${schoolMapImage.data}`} alt="School Map" className="w-full rounded-lg border border-google-grey-200 max-h-[500px] object-contain bg-google-grey-50" data-testid="school-map-image" />
                    <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => handleDeleteImage(schoolMapImage.image_id)} data-testid="delete-school-map-image"><X className="w-4 h-4" /></Button>
                    <p className="text-xs text-google-grey-500 mt-2">{schoolMapImage.filename}</p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-google-grey-300 rounded-lg p-12 text-center hover:border-google-blue hover:bg-blue-50/50 transition-colors cursor-pointer" onClick={() => mapFileRef.current?.click()} data-testid="school-map-upload-area">
                    <Upload className="w-12 h-12 text-google-grey-300 mx-auto mb-3" />
                    <p className="text-google-grey-700 font-medium mb-1">Upload school map</p>
                    <p className="text-sm text-google-grey-400">JPEG or PNG, max 10MB</p>
                  </div>
                )}
                <input ref={mapFileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => handleImageUpload('map', e.target.files?.[0])} data-testid="school-map-file-input" />
                {!schoolMapImage && (
                  <Button variant="outline" className="w-full mt-3 border-google-grey-300" onClick={() => mapFileRef.current?.click()} disabled={uploading.map} data-testid="upload-school-map-btn">
                    {uploading.map ? 'Uploading...' : <><Upload className="w-4 h-4 mr-2" />Choose File</>}
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
